import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Globe, MapPin, Loader2, Sparkles, Phone, PhoneOff, 
  Volume2, Mic, ChevronDown, ChevronUp, Play, Pause,
  Coffee, Wrench, Building2, VolumeX, User, Check,
  ArrowRight, RefreshCw, FileText, ArrowLeft, Settings,
  Lock, Crown, BarChart3, MessageSquare, Calendar, Users,
  Headphones, Clock, Zap, Shield, Bell, Sliders
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { RetellWebClient } from 'retell-client-js-sdk';
import ThemeToggle from '../components/ThemeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Accent categories
const ACCENT_CATEGORIES = [
  { id: 'american', name: 'American', flag: '🇺🇸' },
  { id: 'british', name: 'British', flag: '🇬🇧' },
  { id: 'all', name: 'All Voices', flag: '🌍' },
];

// Language options - Retell officially supported languages
// Source: https://docs.retellai.com/agent/language
const LANGUAGE_OPTIONS = [
  // English Variants
  { id: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { id: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
  { id: 'en-AU', name: 'English (Australia)', flag: '🇦🇺' },
  { id: 'en-IN', name: 'English (India)', flag: '🇮🇳' },
  { id: 'en-NZ', name: 'English (New Zealand)', flag: '🇳🇿' },
  // Spanish Variants
  { id: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
  { id: 'es-419', name: 'Spanish (Latin America)', flag: '🌎' },
  // European Languages
  { id: 'fr-FR', name: 'French', flag: '🇫🇷' },
  { id: 'de-DE', name: 'German', flag: '🇩🇪' },
  { id: 'it-IT', name: 'Italian', flag: '🇮🇹' },
  { id: 'pt-PT', name: 'Portuguese (Portugal)', flag: '🇵🇹' },
  { id: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { id: 'nl-NL', name: 'Dutch', flag: '🇳🇱' },
  { id: 'nl-BE', name: 'Dutch (Belgium)', flag: '🇧🇪' },
  { id: 'pl-PL', name: 'Polish', flag: '🇵🇱' },
  { id: 'ru-RU', name: 'Russian', flag: '🇷🇺' },
  { id: 'uk-UA', name: 'Ukrainian', flag: '🇺🇦' },
  { id: 'sv-SE', name: 'Swedish', flag: '🇸🇪' },
  { id: 'da-DK', name: 'Danish', flag: '🇩🇰' },
  { id: 'no-NO', name: 'Norwegian', flag: '🇳🇴' },
  { id: 'fi-FI', name: 'Finnish', flag: '🇫🇮' },
  { id: 'el-GR', name: 'Greek', flag: '🇬🇷' },
  { id: 'cs-CZ', name: 'Czech', flag: '🇨🇿' },
  { id: 'sk-SK', name: 'Slovak', flag: '🇸🇰' },
  { id: 'hu-HU', name: 'Hungarian', flag: '🇭🇺' },
  { id: 'ro-RO', name: 'Romanian', flag: '🇷🇴' },
  { id: 'bg-BG', name: 'Bulgarian', flag: '🇧🇬' },
  { id: 'hr-HR', name: 'Croatian', flag: '🇭🇷' },
  { id: 'ca-ES', name: 'Catalan', flag: '🏴' },
  // Asian Languages
  { id: 'zh-CN', name: 'Chinese (Mandarin)', flag: '🇨🇳' },
  { id: 'ja-JP', name: 'Japanese', flag: '🇯🇵' },
  { id: 'ko-KR', name: 'Korean', flag: '🇰🇷' },
  { id: 'hi-IN', name: 'Hindi', flag: '🇮🇳' },
  { id: 'th-TH', name: 'Thai', flag: '🇹🇭' },
  { id: 'vi-VN', name: 'Vietnamese', flag: '🇻🇳' },
  { id: 'id-ID', name: 'Indonesian', flag: '🇮🇩' },
  { id: 'ms-MY', name: 'Malay', flag: '🇲🇾' },
  { id: 'tl-PH', name: 'Filipino/Tagalog', flag: '🇵🇭' },
  { id: 'ta-IN', name: 'Tamil', flag: '🇮🇳' },
  // Middle Eastern Languages
  { id: 'tr-TR', name: 'Turkish', flag: '🇹🇷' },
  { id: 'ar-SA', name: 'Arabic', flag: '🇸🇦' },
  { id: 'he-IL', name: 'Hebrew', flag: '🇮🇱' },
  // Multilingual (Auto-detect)
  { id: 'multi', name: 'Multilingual (Auto-Detect)', flag: '🌍' },
];

// Voice options with descriptions - using actual Retell voice IDs
const VOICE_OPTIONS = [
  // American Accents
  { id: '11labs-Lily', name: 'Lily', gender: 'Female', accent: 'american', style: 'Warm & Friendly', preview: 'Young American female', sampleText: 'Hi there! Welcome to our business, how can I help you today?' },
  { id: 'openai-Nova', name: 'Nova', gender: 'Female', accent: 'american', style: 'Professional & Clear', preview: 'Professional American female', sampleText: 'Good afternoon, thank you for calling. How may I assist you?' },
  { id: 'minimax-Ashley', name: 'Ashley', gender: 'Female', accent: 'american', style: 'Energetic & Upbeat', preview: 'Energetic American female', sampleText: 'Hey! Thanks for calling, what can I get for you today?' },
  { id: 'cartesia-Cleo', name: 'Cleo', gender: 'Female', accent: 'american', style: 'Warm & Soothing', preview: 'Warm American female', sampleText: 'Hello, welcome. I am here to help with anything you need.' },
  { id: '11labs-Billy', name: 'Billy', gender: 'Male', accent: 'american', style: 'Casual & Friendly', preview: 'Casual American male', sampleText: 'Hey, what is going on? How can I help you out?' },
  { id: 'minimax-Daniel', name: 'Daniel', gender: 'Male', accent: 'american', style: 'Young & Modern', preview: 'Young American male', sampleText: 'Hi there, thanks for reaching out. What can I do for you?' },
  // British Accents
  { id: '11labs-Dorothy', name: 'Dorothy', gender: 'Female', accent: 'british', style: 'Elegant & Refined', preview: 'Elegant British female', sampleText: 'Good day, welcome to our establishment. How may I be of service?' },
  { id: '11labs-Anthony', name: 'Anthony', gender: 'Male', accent: 'british', style: 'Confident & Professional', preview: 'Professional British male', sampleText: 'Good morning, thank you for calling. How can I assist you today?' },
];

// Background noise options
const BACKGROUND_OPTIONS = [
  { id: 'none', name: 'No Background', icon: VolumeX, description: 'Clean, quiet environment' },
  { id: 'coffee_shop', name: 'Coffee Shop', icon: Coffee, description: 'Café background chatter' },
  { id: 'call_center', name: 'Call Center', icon: Building2, description: 'Busy office atmosphere' },
  { id: 'convention_hall', name: 'Convention Hall', icon: Building2, description: 'Large venue ambiance' },
  { id: 'summer_outdoor', name: 'Summer Outdoor', icon: Volume2, description: 'Warm outdoor setting' },
  { id: 'mountain_outdoor', name: 'Mountain Birds', icon: Volume2, description: 'Nature with birdsong' },
  { id: 'static_noise', name: 'Static Noise', icon: Volume2, description: 'Constant background hum' },
];

// Business categories
const CATEGORY_OPTIONS = [
  { id: 'restaurant', name: 'Restaurant / Food', icon: '🍽️' },
  { id: 'auto_repair', name: 'Auto / Truck Repair', icon: '🔧' },
  { id: 'medical', name: 'Medical / Healthcare', icon: '🏥' },
  { id: 'dental', name: 'Dental Office', icon: '🦷' },
  { id: 'legal', name: 'Law Firm', icon: '⚖️' },
  { id: 'salon', name: 'Salon / Spa', icon: '💇' },
  { id: 'real_estate', name: 'Real Estate', icon: '🏠' },
  { id: 'hvac', name: 'HVAC', icon: '❄️' },
  { id: 'plumbing', name: 'Plumbing', icon: '🔧' },
  { id: 'cleaning', name: 'Cleaning Service', icon: '🧹' },
  { id: 'fitness', name: 'Gym / Fitness', icon: '💪' },
  { id: 'pet', name: 'Pet Services / Vet', icon: '🐕' },
  { id: 'photography', name: 'Photography', icon: '📸' },
  { id: 'landscaping', name: 'Landscaping', icon: '🌳' },
  { id: 'general', name: 'Other / General', icon: '🏢' },
];

const TryDemo = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter URLs, 2: Configure Voice, 3: Test Call, 4: Results
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // No token - redirect to signup silently (they'll see the signup page)
      navigate('/signup');
      return;
    }
    
    // Verify token is valid
    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then((res) => {
      setIsAuthenticated(true);
      setCheckingAuth(false);
      // Pre-fill company name from user's business if available
      if (res.data.business_name) {
        setCompanyName(res.data.business_name);
      }
      // Fetch demo usage
      return axios.get(`${API}/demo/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    })
    .then((usageRes) => {
      if (usageRes?.data) {
        setDemoUsage(usageRes.data);
        if (usageRes.data.limit_reached) {
          setShowLimitModal(true);
        }
      }
    })
    .catch((err) => {
      console.error('Auth check failed:', err);
      localStorage.removeItem('token');
      // Token invalid - redirect to login
      navigate('/login');
    });
  }, [navigate]);
  
  // Form data
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [googleUrl, setGoogleUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  // Business data (from scraping)
  const [businessData, setBusinessData] = useState(null);
  
  // Voice settings
  const [selectedVoice, setSelectedVoice] = useState('11labs-Lily');
  const [selectedBackground, setSelectedBackground] = useState('none');
  const [selectedAccent, setSelectedAccent] = useState('american');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);
  const [voiceTemperature, setVoiceTemperature] = useState(1.0);
  const [showVoiceSettings, setShowVoiceSettings] = useState(true);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loadingVoice, setLoadingVoice] = useState(null);
  const audioRef = useRef(null);
  
  // Call state
  const [callStatus, setCallStatus] = useState('idle');
  const [isCallActive, setIsCallActive] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [callSummary, setCallSummary] = useState(null);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  
  // Editable prompt state
  const [editablePrompt, setEditablePrompt] = useState('');
  const [editableGreeting, setEditableGreeting] = useState('');
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [regeneratingAgent, setRegeneratingAgent] = useState(false);
  
  const retellClientRef = useRef(null);
  
  // Demo usage tracking
  const [demoUsage, setDemoUsage] = useState({ demo_minutes_used: 0, demo_minutes_limit: 45, remaining_minutes: 45, limit_reached: false });
  const [showLimitModal, setShowLimitModal] = useState(false);
  const callStartTimeRef = useRef(null);
  
  // Filter voices by selected accent
  const filteredVoices = selectedAccent === 'all' 
    ? VOICE_OPTIONS 
    : VOICE_OPTIONS.filter(v => v.accent === selectedAccent);

  // Play voice preview
  const playVoicePreview = async (voice) => {
    // Stop current playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // If clicking same voice, just stop
    if (playingVoice === voice.id) {
      setPlayingVoice(null);
      return;
    }
    
    setLoadingVoice(voice.id);
    
    try {
      // Call backend to generate voice sample
      const res = await axios.post(`${API}/demo/voice-preview`, {
        voice_id: voice.id,
        text: voice.sampleText
      });
      
      if (res.data.audio_url) {
        const audio = new Audio(res.data.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingVoice(null);
        };
        
        audio.onerror = () => {
          toast.error('Failed to play voice sample');
          setPlayingVoice(null);
        };
        
        await audio.play();
        setPlayingVoice(voice.id);
      }
    } catch (err) {
      console.error('Voice preview error:', err);
      toast.error('Voice preview not available');
    } finally {
      setLoadingVoice(null);
    }
  };

  // Microphone permission state
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'

  // Proactively check and request microphone permission
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        // First check if we can query permission state
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
            setMicPermission(permissionStatus.state);
            
            // Listen for permission changes
            permissionStatus.onchange = () => {
              setMicPermission(permissionStatus.state);
            };
          } catch (e) {
            // Some browsers don't support microphone permission query
            console.log('Permission query not supported');
          }
        }
      } catch (err) {
        console.log('Error checking mic permission:', err);
      }
    };
    
    checkMicPermission();
  }, []);

  // Request microphone permission proactively when user reaches step 3
  useEffect(() => {
    const requestMicPermission = async () => {
      if (step === 3 && micPermission !== 'granted') {
        try {
          // This will trigger the browser's permission popup
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Stop the stream immediately - we just needed to trigger the permission
          stream.getTracks().forEach(track => track.stop());
          setMicPermission('granted');
          toast.success('Microphone access granted! You can now start the call.');
        } catch (err) {
          console.error('Mic permission request failed:', err);
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setMicPermission('denied');
          }
        }
      }
    };
    
    requestMicPermission();
  }, [step, micPermission]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retellClientRef.current) {
        retellClientRef.current.stopCall();
      }
    };
  }, []);

  // Step 1: Scrape business info
  const handleAnalyzeBusiness = async () => {
    // Company name is required
    if (!companyName) {
      toast.error('Please enter your company name');
      return;
    }

    setScraping(true);
    try {
      const res = await axios.post(`${API}/demo/analyze-business`, {
        website_url: websiteUrl || null,
        google_url: googleUrl || null,
        company_name: companyName  // Pass the company name
      });
      
      // Override business_name with user-provided company name
      setBusinessData({
        ...res.data,
        business_name: companyName
      });
      toast.success('Business analyzed successfully!');
      setStep(2);
    } catch (err) {
      console.error('Analysis error:', err);
      toast.error(err.response?.data?.detail || 'Failed to analyze business. Please check the URL.');
    } finally {
      setScraping(false);
    }
  };

  // Step 2: Configure and proceed to call
  const handleProceedToCall = async () => {
    setLoading(true);
    try {
      // Create temporary demo agent with selected voice and background
      const res = await axios.post(`${API}/demo/create-temp-agent`, {
        business_data: businessData,
        voice_id: selectedVoice,
        background_noise: selectedBackground,
        voice_speed: voiceSpeed,
        voice_temperature: voiceTemperature,
        language: selectedLanguage
      });
      
      if (res.data.agent_id) {
        setBusinessData(prev => ({ ...prev, temp_agent_id: res.data.agent_id }));
        // Save demo agent ID for potential account creation
        sessionStorage.setItem('demo_agent_id', res.data.agent_id);
        // Initialize editable prompt with the generated values
        setEditablePrompt(businessData?.personality || '');
        setEditableGreeting(businessData?.greeting || '');
        // Show prompt by default on Step 3
        setShowPrompt(true);
        toast.success('Pro created!');
        setStep(3);
      } else {
        throw new Error('No agent ID returned');
      }
    } catch (err) {
      console.error('Create agent error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create demo agent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update voice settings and regenerate agent
  const handleUpdateVoiceSettings = async () => {
    setRegeneratingAgent(true);
    try {
      const res = await axios.post(`${API}/demo/create-temp-agent`, {
        business_data: {
          ...businessData,
          personality: editablePrompt || businessData?.personality,
          greeting: editableGreeting || businessData?.greeting
        },
        voice_id: selectedVoice,
        background_noise: selectedBackground,
        voice_speed: voiceSpeed,
        voice_temperature: voiceTemperature,
        custom_prompt: editablePrompt || null,
        custom_greeting: editableGreeting || null,
        language: selectedLanguage
      });
      
      if (res.data.agent_id) {
        setBusinessData(prev => ({ ...prev, temp_agent_id: res.data.agent_id }));
        // Update demo agent ID for potential account creation
        sessionStorage.setItem('demo_agent_id', res.data.agent_id);
        toast.success('Voice settings updated!');
      } else {
        throw new Error('No agent ID returned');
      }
    } catch (err) {
      console.error('Update voice settings error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update voice settings.');
    } finally {
      setRegeneratingAgent(false);
    }
  };

  // Regenerate agent with edited prompt
  const handleRegenerateAgent = async () => {
    if (!editablePrompt.trim()) {
      toast.error('Please enter a prompt for your Pro');
      return;
    }
    
    setRegeneratingAgent(true);
    try {
      // Update businessData with edited prompt
      const updatedBusinessData = {
        ...businessData,
        personality: editablePrompt,
        greeting: editableGreeting || businessData?.greeting
      };
      
      // Create new agent with edited prompt
      const res = await axios.post(`${API}/demo/create-temp-agent`, {
        business_data: updatedBusinessData,
        voice_id: selectedVoice,
        background_noise: selectedBackground,
        custom_prompt: editablePrompt,
        custom_greeting: editableGreeting,
        voice_speed: voiceSpeed,
        voice_temperature: voiceTemperature,
        language: selectedLanguage
      });
      
      if (res.data.agent_id) {
        setBusinessData(prev => ({ 
          ...prev, 
          temp_agent_id: res.data.agent_id,
          personality: editablePrompt,
          greeting: editableGreeting || prev?.greeting
        }));
        setIsEditingPrompt(false);
        toast.success('Pro regenerated with your custom prompt!');
      } else {
        throw new Error('No agent ID returned');
      }
    } catch (err) {
      console.error('Regenerate agent error:', err);
      toast.error(err.response?.data?.detail || 'Failed to regenerate agent. Please try again.');
    } finally {
      setRegeneratingAgent(false);
    }
  };

  // Step 3: Start the test call
  const startCall = async () => {
    // Check if user has reached demo limit
    if (demoUsage.limit_reached) {
      setShowLimitModal(true);
      return;
    }
    
    try {
      setCallStatus('connecting');
      setTranscript([]);
      
      // Check microphone permission first
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
        if (permissionStatus.state === 'denied') {
          toast.error(
            'Microphone access denied. Please enable it in your browser settings:\n' +
            '1. Click the 🔒 or ⚙️ icon in your address bar\n' +
            '2. Find "Microphone" and set it to "Allow"\n' +
            '3. Refresh the page and try again',
            { duration: 10000 }
          );
          setCallStatus('idle');
          return;
        }
      } catch (permErr) {
        // Some browsers don't support permissions.query for microphone
        console.log('Permission query not supported, will try getUserMedia');
      }
      
      // Request microphone access explicitly before starting call
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the test stream immediately after permission is granted
        stream.getTracks().forEach(track => track.stop());
      } catch (micErr) {
        console.error('Microphone access error:', micErr);
        if (micErr.name === 'NotAllowedError' || micErr.name === 'PermissionDeniedError') {
          toast.error(
            'Microphone access was blocked. To enable:\n' +
            '• Chrome/Edge: Click 🔒 icon → Site settings → Microphone → Allow\n' +
            '• Firefox: Click 🔒 icon → Connection secure → More Info → Permissions\n' +
            '• Safari: Settings → Websites → Microphone → Allow',
            { duration: 10000 }
          );
        } else if (micErr.name === 'NotFoundError') {
          toast.error('No microphone found. Please connect a microphone and try again.');
        } else {
          toast.error('Could not access microphone. Please check your browser settings.');
        }
        setCallStatus('idle');
        return;
      }
      
      // Get web call access token
      const res = await axios.post(`${API}/demo/web-call`, {
        agent_id: businessData.temp_agent_id,
        voice_id: selectedVoice,
        background_noise: selectedBackground,
        business_name: businessData.business_name
      });

      // Initialize Retell client
      retellClientRef.current = new RetellWebClient();
      
      // Event listeners
      retellClientRef.current.on('call_started', () => {
        setCallStatus('connected');
        setIsCallActive(true);
        // Track call start time for usage calculation
        callStartTimeRef.current = Date.now();
        toast.success('Call connected! Start speaking...');
      });

      retellClientRef.current.on('call_ended', () => {
        setCallStatus('idle');
        setIsCallActive(false);
        // Track demo usage when call ends
        trackDemoUsage();
        generateCallSummary();
      });

      retellClientRef.current.on('agent_start_talking', () => {
        setCallStatus('speaking');
      });

      retellClientRef.current.on('agent_stop_talking', () => {
        setCallStatus('listening');
      });

      retellClientRef.current.on('update', (update) => {
        if (update.transcript) {
          setTranscript(update.transcript);
        }
      });

      retellClientRef.current.on('error', (error) => {
        console.error('Retell error:', error);
        // Track usage even on error
        trackDemoUsage();
        if (error.message?.includes('microphone') || error.message?.includes('permission')) {
          toast.error('Microphone error. Please check your browser permissions.');
        } else {
          toast.error('Call error occurred. Please try again.');
        }
        setCallStatus('idle');
        setIsCallActive(false);
      });

      // Start the call
      await retellClientRef.current.startCall({
        accessToken: res.data.access_token,
        callId: res.data.call_id,
        transport: res.data.transport,
        iceServers: res.data.ice_servers,
        sampleRate: 24000,
        captureDeviceId: 'default'
      });

    } catch (err) {
      console.error('Call error:', err);
      toast.error('Failed to start call. Please try again.');
      setCallStatus('idle');
    }
  };
  
  // Track demo usage after call ends
  const trackDemoUsage = async () => {
    if (!callStartTimeRef.current) return;
    
    const callDurationSeconds = (Date.now() - callStartTimeRef.current) / 1000;
    callStartTimeRef.current = null;
    
    if (callDurationSeconds < 1) return; // Ignore very short calls
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await axios.post(`${API}/demo/usage/update`, 
        { call_duration_seconds: callDurationSeconds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data) {
        setDemoUsage(res.data);
        if (res.data.limit_reached) {
          // Show modal after call ends if limit is now reached
          setTimeout(() => setShowLimitModal(true), 1500);
        }
      }
    } catch (err) {
      console.error('Failed to track demo usage:', err);
    }
  };

  const endCall = () => {
    if (retellClientRef.current) {
      retellClientRef.current.stopCall();
    }
    setCallStatus('idle');
    setIsCallActive(false);
    // Track usage when manually ending call
    trackDemoUsage();
    generateCallSummary();
  };

  const generateCallSummary = () => {
    if (transcript.length > 0) {
      // Extract key points from transcript
      const keyPoints = [];
      const agentMessages = transcript.filter(t => t.role === 'agent');
      const userMessages = transcript.filter(t => t.role === 'user');
      
      if (userMessages.length > 0) {
        keyPoints.push(`Customer asked about: ${userMessages.slice(0, 2).map(m => m.content.slice(0, 50)).join(', ')}`);
      }
      if (agentMessages.length > 0) {
        keyPoints.push(`Pro provided: Professional greeting and assistance`);
      }
      
      setCallSummary({
        duration: transcript.length * 2, // Rough estimate
        keyPoints: keyPoints.length > 0 ? keyPoints : ['Demo call completed'],
        sentiment: 'positive'
      });
      setStep(4);
    }
  };

  // Get status display
  const getStatusDisplay = () => {
    switch (callStatus) {
      case 'connecting': return { text: 'Connecting...', color: 'text-yellow-400' };
      case 'connected': return { text: 'Connected', color: 'text-green-400' };
      case 'speaking': return { text: 'Pro Speaking...', color: 'text-blue-400' };
      case 'listening': return { text: 'Listening...', color: 'text-purple-400' };
      default: return { text: 'Ready', color: 'text-muted-foreground' };
    }
  };

  const statusDisplay = getStatusDisplay();

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, this will redirect (handled in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">
              Front<span className="text-primary">DeskPro</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Button variant="ghost" onClick={() => navigate('/dashboard')}>Dashboard</Button>
                <Button variant="outline" onClick={() => {
                  localStorage.removeItem('token');
                  navigate('/');
                }}>Sign Out</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')}>Sign In</Button>
                <Button onClick={() => navigate('/pricing')}>View Pricing</Button>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button + Progress Steps */}
        <div className="flex items-center mb-8">
          {/* Back Arrow */}
          {step > 1 && (
            <button
              onClick={() => setStep(s => s - 1)}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Back</span>
            </button>
          )}
          {step === 1 && (
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors mr-4"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Home</span>
            </button>
          )}
          
          {/* Progress Steps - Centered */}
          <div className="flex-1 flex items-center justify-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div 
                  onClick={() => s < step && setStep(s)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                  } ${s < step ? 'cursor-pointer hover:opacity-80' : ''}`}
                >
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`w-8 sm:w-12 h-1 rounded ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
              </React.Fragment>
            ))}
          </div>
          
          {/* Spacer for symmetry */}
          <div className="w-16" />
        </div>

        {/* Step 1: Enter URLs */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Try Your Front Desk Pro <span className="text-primary">Free</span>
              </h1>
              <p className="text-muted-foreground">
                Enter your business details and we'll create a custom Pro in seconds
              </p>
            </div>

            {/* Demo Disclaimer Banner */}
            <Card className="bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border-violet-500/30 p-4 max-w-xl mx-auto">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm mb-1">🎯 This is a Quick Demo Preview</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    What you're about to experience is a <span className="text-foreground font-medium">sneak peek</span> of what your Pro could sound like. 
                    When you sign up, our team will <span className="text-violet-400 font-medium">personally configure</span> your Pro with:
                  </p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-green-400" />
                      Custom wording tailored to your business style
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-green-400" />
                      Trained on your exact services, pricing & hours
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-green-400" />
                      Perfected through test calls before going live
                    </li>
                  </ul>
                  <p className="text-xs text-violet-400 font-medium mt-2">
                    The real thing will be 10x better! ✨
                  </p>
                </div>
              </div>
            </Card>

            <Card className="card-modern p-6 max-w-xl mx-auto">
              <div className="space-y-4">
                {/* Company Name Input */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Company Name *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="input-company-name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Your Business Name"
                      className="pl-10 bg-muted border-border"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">How your Pro will greet callers</p>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Website URL</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="input-website"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://yourbusiness.com"
                      className="pl-10 bg-muted border-border"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Your business website (optional)</p>
                </div>

                <div className="flex items-center gap-4 my-2">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-xs text-muted-foreground">OR</span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Google Business Page</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="input-google"
                      value={googleUrl}
                      onChange={(e) => setGoogleUrl(e.target.value)}
                      placeholder="https://maps.google.com/..."
                      className="pl-10 bg-muted border-border"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Works if you only have a Google listing (optional)</p>
                </div>

                <Button 
                  data-testid="analyze-btn"
                  onClick={handleAnalyzeBusiness}
                  disabled={scraping || !companyName}
                  className="w-full bg-primary hover:bg-primary/90 mt-4"
                >
                  {scraping ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Your Business...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Front Desk Pro
                    </>
                  )}
                </Button>
              </div>
            </Card>

            <p className="text-center text-sm text-muted-foreground">
              No signup required • Free to try • Takes 30 seconds
            </p>
          </div>
        )}

        {/* Step 2: Configure Voice */}
        {step === 2 && businessData && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Customize Your Pro Voice
              </h1>
              <p className="text-muted-foreground">
                Choose how your receptionist sounds
              </p>
            </div>

            {/* Business Preview - Editable */}
            <Card className="card-modern p-4 mb-6">
              {editingBusiness ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Business Name</label>
                    <Input
                      value={businessData.business_name}
                      onChange={(e) => setBusinessData(d => ({ ...d, business_name: e.target.value }))}
                      className="bg-muted border-border"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingBusiness(false)}>
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{businessData.business_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Your Pro
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setEditingBusiness(true)}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Edit
                  </Button>
                </div>
              )}
            </Card>

            {/* Voice Selection */}
            <div>
              <h3 className="font-semibold text-foreground mb-3">Select Voice</h3>
              
              {/* Language Selection */}
              <div className="mb-4">
                <label className="text-sm font-medium text-foreground mb-2 block">Language</label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg bg-background border border-border text-foreground cursor-pointer text-base"
                  data-testid="language-select"
                >
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
                {selectedLanguage !== 'en-US' && selectedLanguage !== 'multi' && (
                  <p className="text-xs text-amber-500 mt-2">
                    ⚠️ Note: For best results with {LANGUAGE_OPTIONS.find(l => l.id === selectedLanguage)?.name}, your Pro prompt should also be in that language.
                  </p>
                )}
              </div>

              {/* Accent Filter */}
              <div className="flex gap-2 mb-4">
                {ACCENT_CATEGORIES.map((accent) => (
                  <button
                    key={accent.id}
                    onClick={() => {
                      setSelectedAccent(accent.id);
                      // If current voice isn't in new filter, select first available
                      const newFiltered = accent.id === 'all' 
                        ? VOICE_OPTIONS 
                        : VOICE_OPTIONS.filter(v => v.accent === accent.id);
                      if (!newFiltered.find(v => v.id === selectedVoice)) {
                        setSelectedVoice(newFiltered[0]?.id || selectedVoice);
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      selectedAccent === accent.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <span>{accent.flag}</span>
                    <span>{accent.name}</span>
                  </button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {filteredVoices.map((voice) => (
                  <Card
                    key={voice.id}
                    data-testid={`voice-${voice.id}`}
                    onClick={() => setSelectedVoice(voice.id)}
                    className={`p-3 cursor-pointer transition-all relative ${
                      selectedVoice === voice.id 
                        ? 'ring-2 ring-primary border-primary' 
                        : 'hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className={`w-4 h-4 ${voice.gender === 'Female' ? 'text-pink-400' : 'text-blue-400'}`} />
                        <span className="font-medium text-foreground text-sm">{voice.name}</span>
                      </div>
                      {/* Play Preview Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playVoicePreview(voice);
                        }}
                        className={`p-1.5 rounded-full transition-all ${
                          playingVoice === voice.id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                        data-testid={`play-voice-${voice.id}`}
                        title="Preview voice"
                      >
                        {loadingVoice === voice.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : playingVoice === voice.id ? (
                          <Pause className="w-3 h-3" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">{voice.style}</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">{voice.preview}</p>
                    {selectedVoice === voice.id && (
                      <Check className="w-4 h-4 text-primary absolute bottom-2 right-2" />
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Background Noise */}
            <div>
              <h3 className="font-semibold text-foreground mb-3">Background Ambiance</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {BACKGROUND_OPTIONS.map((bg) => (
                  <Card
                    key={bg.id}
                    data-testid={`bg-${bg.id}`}
                    onClick={() => setSelectedBackground(bg.id)}
                    className={`p-3 cursor-pointer transition-all ${
                      selectedBackground === bg.id 
                        ? 'ring-2 ring-primary border-primary' 
                        : 'hover:border-primary/50'
                    }`}
                  >
                    <bg.icon className={`w-5 h-5 mb-2 ${selectedBackground === bg.id ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="font-medium text-foreground text-sm">{bg.name}</p>
                    <p className="text-xs text-muted-foreground">{bg.description}</p>
                  </Card>
                ))}
              </div>
            </div>

            <Button 
              onClick={handleProceedToCall}
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <>
                  Continue to Voice Test <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 3: Test Call */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Test Your Front Desk Pro
              </h1>
              <p className="text-muted-foreground">
                Click the button below to start a test call with your custom Pro
              </p>
            </div>

            {/* Demo Reminder Banner */}
            <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 p-4 max-w-md mx-auto">
              <div className="flex items-center gap-3">
                <div className="text-2xl">🎬</div>
                <div>
                  <p className="text-sm text-foreground font-medium">Remember: This is just a preview!</p>
                  <p className="text-xs text-muted-foreground">
                    Your actual Pro will be <span className="text-amber-400">professionally trained</span> with your exact business details, tone, and responses.
                  </p>
                </div>
              </div>
            </Card>

            {/* Demo Time Remaining Indicator */}
            <div className="max-w-md mx-auto">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Demo Time
                </span>
                <span className={`font-medium ${demoUsage.remaining_minutes <= 5 ? 'text-amber-500' : 'text-foreground'}`}>
                  {demoUsage.remaining_minutes.toFixed(1)} min remaining
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${demoUsage.remaining_minutes <= 5 ? 'bg-amber-500' : 'bg-primary'}`}
                  style={{ width: `${(demoUsage.remaining_minutes / demoUsage.demo_minutes_limit) * 100}%` }}
                />
              </div>
              {demoUsage.remaining_minutes <= 10 && !demoUsage.limit_reached && (
                <p className="text-xs text-amber-500 mt-1 text-center">
                  Running low on demo time? <button onClick={() => navigate('/pricing')} className="underline font-medium">Subscribe now</button> for unlimited access.
                </p>
              )}
            </div>

            {/* Call Interface */}
            <Card className="card-modern p-8 max-w-md mx-auto text-center">
              {/* Business Info */}
              <div className="mb-6">
                <h3 className="font-semibold text-foreground text-lg">{businessData?.business_name}</h3>
                <p className="text-sm text-muted-foreground">
                  Voice: {VOICE_OPTIONS.find(v => v.id === selectedVoice)?.name} • 
                  {BACKGROUND_OPTIONS.find(b => b.id === selectedBackground)?.name}
                </p>
              </div>

              {/* Call Button */}
              <div className="relative mb-6">
                <button
                  data-testid="call-btn"
                  onClick={isCallActive ? endCall : startCall}
                  disabled={callStatus === 'connecting'}
                  className={`w-24 h-24 rounded-full flex items-center justify-center transition-all mx-auto ${
                    isCallActive 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : 'bg-primary hover:bg-primary/90'
                  } ${callStatus === 'connecting' ? 'opacity-50' : ''}`}
                >
                  {isCallActive ? (
                    <PhoneOff className="w-10 h-10 text-white" />
                  ) : (
                    <Phone className="w-10 h-10 text-white" />
                  )}
                </button>
                
                {/* Ripple effect when active */}
                {isCallActive && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-32 h-32 rounded-full border-2 border-primary/30 animate-ping" />
                  </div>
                )}
              </div>

              {/* Status */}
              <p className={`text-sm font-medium ${statusDisplay.color}`}>
                {statusDisplay.text}
              </p>

              {/* Instructions based on mic permission state */}
              {!isCallActive && callStatus === 'idle' && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Click to start • Ask about services, hours, or book an appointment
                  </p>
                  
                  {/* Mic Permission Status */}
                  {micPermission === 'granted' ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-xs">
                      <p className="font-medium text-green-400 mb-1">✅ Microphone Ready</p>
                      <p className="text-green-400/80">
                        Your microphone is connected. Click the button above to start your test call!
                      </p>
                    </div>
                  ) : micPermission === 'denied' ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs">
                      <p className="font-medium text-red-400 mb-1">❌ Microphone Blocked</p>
                      <p className="text-red-400/80 mb-2">
                        Your browser has blocked microphone access. To enable it:
                      </p>
                      <ol className="text-red-400/80 list-decimal list-inside space-y-1">
                        <li>Click the 🔒 or ⚙️ icon in your address bar (left of the URL)</li>
                        <li>Find "Microphone" in the site settings</li>
                        <li>Change it from "Block" to "Allow"</li>
                        <li>Refresh this page</li>
                      </ol>
                    </div>
                  ) : (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs">
                      <p className="font-medium text-blue-400 mb-1">🎤 Microphone Permission Needed</p>
                      <p className="text-blue-400/80 mb-2">
                        A popup should appear asking for microphone access. <strong>Click "Allow"</strong> to continue.
                      </p>
                      <p className="text-blue-400/80">
                        <strong>Not seeing the popup?</strong> Click the 🔒 icon in your address bar and enable microphone access, then refresh the page.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Live Transcript Preview */}
              {transcript.length > 0 && (
                <div className="mt-6 text-left max-h-40 overflow-y-auto">
                  {transcript.slice(-4).map((t, i) => (
                    <div key={i} className={`text-sm mb-2 ${t.role === 'agent' ? 'text-primary' : 'text-foreground'}`}>
                      <span className="font-medium">{t.role === 'agent' ? 'Pro: ' : 'You: '}</span>
                      {t.content}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <p className="text-center text-sm text-muted-foreground">
              Speak naturally • Your Pro will respond based on your business info
            </p>

            {/* Voice Settings Section */}
            <div className="max-w-2xl mx-auto mt-6">
              <button
                onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
              >
                <Settings className="w-4 h-4" />
                {showVoiceSettings ? 'Hide' : 'Show'} Voice Settings
                {showVoiceSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showVoiceSettings && (
                <Card className="mt-4 p-4 bg-muted/30 border-muted">
                  <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-primary" />
                    Voice Settings
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Voice Selection */}
                    <div>
                      <label className="text-xs font-medium text-foreground block mb-2">Voice:</label>
                      <select
                        value={selectedVoice}
                        onChange={(e) => setSelectedVoice(e.target.value)}
                        className="w-full p-2 text-sm bg-background border border-border rounded-lg"
                        data-testid="voice-select"
                      >
                        {VOICE_OPTIONS.map(voice => (
                          <option key={voice.id} value={voice.id}>
                            {voice.name} ({voice.accent === 'american' ? '🇺🇸' : '🇬🇧'} {voice.gender})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Voice Speed */}
                    <div>
                      <label className="text-xs font-medium text-foreground block mb-2">
                        Speed: {voiceSpeed.toFixed(1)}x
                        <span className="text-muted-foreground ml-2">
                          {voiceSpeed < 0.9 ? '(Slower)' : voiceSpeed > 1.1 ? '(Faster)' : '(Normal)'}
                        </span>
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="1.5"
                        step="0.1"
                        value={voiceSpeed}
                        onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="voice-speed-slider"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0.5x</span>
                        <span>1.0x</span>
                        <span>1.5x</span>
                      </div>
                    </div>
                    
                    {/* Voice Temperature/Emotion */}
                    <div>
                      <label className="text-xs font-medium text-foreground block mb-2">
                        Emotion/Variation: {voiceTemperature.toFixed(1)}
                        <span className="text-muted-foreground ml-2">
                          {voiceTemperature < 0.8 ? '(Monotone)' : voiceTemperature > 1.2 ? '(Expressive)' : '(Natural)'}
                        </span>
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="1.5"
                        step="0.1"
                        value={voiceTemperature}
                        onChange={(e) => setVoiceTemperature(parseFloat(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="voice-temperature-slider"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>Monotone</span>
                        <span>Natural</span>
                        <span>Expressive</span>
                      </div>
                    </div>
                    
                    {/* Background Noise */}
                    <div>
                      <label className="text-xs font-medium text-foreground block mb-2">Background:</label>
                      <select
                        value={selectedBackground}
                        onChange={(e) => setSelectedBackground(e.target.value)}
                        className="w-full p-2 text-sm bg-background border border-border rounded-lg"
                        data-testid="background-select"
                      >
                        {BACKGROUND_OPTIONS.map(bg => (
                          <option key={bg.id} value={bg.id}>{bg.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Apply Button */}
                    <Button 
                      onClick={handleUpdateVoiceSettings}
                      disabled={regeneratingAgent}
                      className="w-full"
                      data-testid="apply-voice-settings-btn"
                    >
                      {regeneratingAgent ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {regeneratingAgent ? 'Applying...' : 'Apply Voice Settings'}
                    </Button>
                  </div>
                </Card>
              )}
            </div>

            {/* Show/Edit Generated Prompt */}
            <div className="max-w-2xl mx-auto mt-6">
              <button
                onClick={() => {
                  setShowPrompt(!showPrompt);
                  if (!showPrompt && businessData?.personality) {
                    setEditablePrompt(businessData.personality);
                    setEditableGreeting(businessData.greeting || '');
                  }
                }}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
              >
                <FileText className="w-4 h-4" />
                {showPrompt ? 'Hide' : 'View & Edit'} Prompt
                {showPrompt ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showPrompt && (
                <Card className="mt-4 p-4 bg-muted/30 border-muted">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Pro Personality Prompt
                    </h4>
                    {!isEditingPrompt ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsEditingPrompt(true)}
                        data-testid="edit-prompt-btn"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" /> Edit & Customize
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setIsEditingPrompt(false);
                            setEditablePrompt(businessData?.personality || '');
                            setEditableGreeting(businessData?.greeting || '');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm"
                          onClick={handleRegenerateAgent}
                          disabled={regeneratingAgent}
                          data-testid="regenerate-agent-btn"
                        >
                          {regeneratingAgent ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Check className="w-3 h-3 mr-1" />
                          )}
                          {regeneratingAgent ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Greeting Section */}
                  <div className="mb-4">
                    <label className="text-xs font-medium text-foreground block mb-1">Opening Greeting:</label>
                    {isEditingPrompt ? (
                      <Input
                        value={editableGreeting}
                        onChange={(e) => setEditableGreeting(e.target.value)}
                        placeholder="Thanks for calling [Business], how can I help you?"
                        className="text-sm bg-background"
                        data-testid="edit-greeting-input"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground bg-background/50 rounded p-2">
                        "{businessData?.greeting}"
                      </p>
                    )}
                  </div>
                  
                  {/* Prompt Section */}
                  <div>
                    <label className="text-xs font-medium text-foreground block mb-1">
                      Personality & Instructions:
                    </label>
                    {isEditingPrompt ? (
                      <textarea
                        value={editablePrompt}
                        onChange={(e) => setEditablePrompt(e.target.value)}
                        className="w-full h-64 p-3 text-xs font-mono bg-background border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="Enter your Pro's personality, instructions, and any business-specific information..."
                        data-testid="edit-prompt-textarea"
                      />
                    ) : (
                      <div className="bg-background/50 rounded-lg p-3 max-h-64 overflow-y-auto">
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                          {businessData?.personality}
                        </pre>
                      </div>
                    )}
                  </div>
                  
                  {isEditingPrompt && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Tip: You can customize how your Pro talks, what information it knows, and its personality!
                    </p>
                  )}
                  
                  {!isEditingPrompt && businessData?.scraped_content?.menu_items?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-muted">
                      <p className="text-xs font-medium text-foreground mb-1">Menu Items Found:</p>
                      <ul className="text-xs text-muted-foreground">
                        {businessData.scraped_content.menu_items.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              )}
            </div>

            {/* Premium Features Preview - Teaser Section */}
            <div className="max-w-4xl mx-auto mt-8">
              <div className="relative">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-4 py-2 rounded-full mb-3">
                    <Crown className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Premium Features</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Unlock the Full Power After Signing Up
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    This demo is just the beginning. Here's what you get with a full account:
                  </p>
                </div>

                {/* Premium Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Feature 1 - Dedicated Phone Number */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Dedicated Phone Number</h4>
                        <p className="text-xs text-muted-foreground mt-1">Get your own business number that customers can call 24/7</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 2 - Call Analytics */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Call Analytics Dashboard</h4>
                        <p className="text-xs text-muted-foreground mt-1">Track call volume, duration, peak hours & customer insights</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 3 - SMS Integration */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">SMS Notifications</h4>
                        <p className="text-xs text-muted-foreground mt-1">Get text alerts for every call + send follow-up messages</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 4 - Calendar Integration */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-purple-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Calendar Booking</h4>
                        <p className="text-xs text-muted-foreground mt-1">Pro books appointments directly to Google/Outlook calendar</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 5 - Call Recording */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                        <Headphones className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Call Recordings</h4>
                        <p className="text-xs text-muted-foreground mt-1">Listen back to every call with full transcripts & summaries</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 6 - Business Hours */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-orange-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Business Hours Rules</h4>
                        <p className="text-xs text-muted-foreground mt-1">Set different greetings & behaviors for open/closed hours</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 7 - Call Transfer */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-5 h-5 text-cyan-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Live Call Transfer</h4>
                        <p className="text-xs text-muted-foreground mt-1">Pro transfers urgent calls to you or your team instantly</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 8 - Team Access */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-indigo-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Team Members</h4>
                        <p className="text-xs text-muted-foreground mt-1">Add staff members with their own access & notifications</p>
                      </div>
                    </div>
                  </Card>

                  {/* Feature 9 - Advanced Pro Training */}
                  <Card className="p-4 bg-muted/20 border-muted relative overflow-hidden group hover:border-primary/50 transition-all">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center flex-shrink-0">
                        <Sliders className="w-5 h-5 text-pink-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">Advanced Pro Training</h4>
                        <p className="text-xs text-muted-foreground mt-1">Upload menus, FAQs, policies - Pro learns your business</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* CTA Section */}
                <div className="mt-8 text-center">
                  <Card className="inline-block p-6 bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/30">
                    <p className="text-sm text-muted-foreground mb-3">
                      Ready to never miss a customer call again?
                    </p>
                    <Button 
                      onClick={() => navigate('/pricing')}
                      className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                      data-testid="unlock-premium-btn"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      View Plans & Pricing
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3">
                      Starter or Pro plans • We build your custom Pro
                    </p>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Demo Complete!
              </h1>
              <p className="text-muted-foreground">
                Here's a summary of your test call
              </p>
            </div>

            {/* The Real Thing Will Be Better Banner */}
            <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30 p-5 max-w-xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="text-3xl">🚀</div>
                <div>
                  <h3 className="font-bold text-foreground mb-2">
                    Imagine This, But 10x Better
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    What you just experienced was a <span className="text-green-400 font-medium">quick demo</span> based on publicly available info. 
                    When you sign up, here's what happens:
                  </p>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        <span className="text-foreground font-medium">Custom Script:</span> We write the exact wording that matches YOUR business tone
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        <span className="text-foreground font-medium">Deep Training:</span> Pro learns your services, prices, FAQs, and special offers
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        <span className="text-foreground font-medium">Test Calls:</span> We make test calls and refine until it's perfect
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        <span className="text-foreground font-medium">Live Support:</span> We're here to tweak and improve anytime
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Summary Card */}
            <Card className="card-modern p-6 max-w-xl mx-auto">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Call Summary
              </h3>
              
              {callSummary && (
                <div className="space-y-4">
                  {/* Key Points */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Key Points:</p>
                    <ul className="space-y-2">
                      {callSummary.keyPoints.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-foreground">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Full Transcript Toggle */}
                  <button
                    onClick={() => setShowFullTranscript(!showFullTranscript)}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    {showFullTranscript ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {showFullTranscript ? 'Hide' : 'View'} Full Conversation
                  </button>

                  {/* Full Transcript */}
                  {showFullTranscript && transcript.length > 0 && (
                    <div className="bg-muted rounded-lg p-4 max-h-60 overflow-y-auto">
                      {transcript.map((t, i) => (
                        <div key={i} className={`mb-3 ${t.role === 'agent' ? 'text-primary' : 'text-foreground'}`}>
                          <span className="font-medium text-xs uppercase">{t.role === 'agent' ? 'PRO' : 'You'}</span>
                          <p className="text-sm">{t.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-xl mx-auto">
              <Button 
                variant="outline" 
                onClick={() => { setStep(3); setTranscript([]); setCallSummary(null); }}
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Try Again
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                className="flex-1 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
              >
                Get the Real Thing <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <Card className="max-w-xl mx-auto p-4 bg-muted/30 border-muted text-center">
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">What happens next?</span> After signup, our team schedules a 15-min call to learn about your business. 
                Within 24 hours, your custom Pro is live and answering calls.
              </p>
              <p className="text-xs text-primary mt-2 font-medium">
                Simple monthly plans • Personal Pro training included
              </p>
            </Card>
          </div>
        )}
      </div>
      
      {/* Demo Limit Reached Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 bg-background border-border">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Demo Time Limit Reached
              </h2>
              <p className="text-muted-foreground">
                You've used all <span className="font-semibold text-foreground">{demoUsage.demo_minutes_limit} minutes</span> of your free demo time.
              </p>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Ready to Get Started?
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Subscribe now to get your own Pro with:
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2 text-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  Dedicated phone number for your business
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  Up to 400 minutes included monthly
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  24/7 Pro, never miss a call
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Check className="w-4 h-4 text-green-500" />
                  Personal Pro training by our team
                </li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/pricing')}
                className="w-full bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 text-white"
                data-testid="subscribe-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                View Plans & Subscribe
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowLimitModal(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
            
            <p className="text-xs text-center text-muted-foreground mt-4">
              Starting at just $5/month for testing
            </p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TryDemo;
